import { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  alpha,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useAuth } from "../contexts/AuthContext";
import { tokens } from "../theme";

export default function LoginPage() {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setError(null);
    const ok = await login(password);
    setSubmitting(false);
    if (!ok) {
      setError("パスワードが正しくありません");
      setPassword("");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-30%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "120%",
          height: "80%",
          background: `radial-gradient(circle, ${alpha(tokens.accent, 0.15)} 0%, transparent 60%)`,
          filter: "blur(120px)",
          pointerEvents: "none",
        },
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 380,
          p: { xs: 3, sm: 4 },
          position: "relative",
          backdropFilter: "blur(20px) saturate(180%)",
          backgroundColor: alpha(tokens.paper, 0.7),
        }}
      >
        <Stack spacing={3} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              border: `1px solid ${tokens.borderStrong}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha("#fff", 0.02),
            }}
          >
            <LockOutlinedIcon sx={{ color: tokens.accent, fontSize: 22 }} />
          </Box>

          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              OCR Studio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              アクセスパスワードを入力してください
            </Typography>
          </Stack>

          <TextField
            fullWidth
            autoFocus
            type={show ? "text" : "password"}
            placeholder="パスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShow((v) => !v)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert
              severity="error"
              sx={{
                width: "100%",
                py: 0.5,
                fontSize: 13,
                bgcolor: alpha("#EF4444", 0.08),
                border: `1px solid ${alpha("#EF4444", 0.2)}`,
                "& .MuiAlert-icon": { fontSize: 18 },
              }}
            >
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting || !password}
            endIcon={!submitting && <ArrowForwardIcon />}
            sx={{ py: 1.2, fontWeight: 500 }}
          >
            {submitting ? "確認中..." : "サインイン"}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", lineHeight: 1.6 }}
          >
            このツールは API キー保護のため認証が必要です。<br />
            パスワードはブラウザにのみ保存されます。
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
